// Academy MARKETING page — public, white background, sales-oriented.
// Shows value prop, audiences, how-it-works, series preview (locked) + CTA.
// Active subscribers are auto-redirected to /academy/watch (Netflix experience).
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, Lock, Check, Film, GraduationCap,
  Users, BookOpen, Award, Clock,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import AcademySubAppsStory from "@/components/AcademySubAppsStory";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";
const TRIAL_DAYS = 7;
const REGISTER_URL = "https://app.geovinum.io";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const tk = raw ? (JSON.parse(raw)?.token) : null;
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  } catch { return {}; }
}

const fmtDuration = (sec) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}min` : `${m} min`;
};

const sub = (s, vars) => Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

const AcademyPage = () => {
  const t = useT();
  const { user } = useAuth();
  const [hasSub, setHasSub] = useState(null);
  const [previewSeries, setPreviewSeries] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { if (!cancelled) setHasSub(false); return; }
      const { ok, data } = await apiFetch(`/api/auth/me/subscription`, { headers: authHeaders() });
      if (!cancelled) setHasSub(ok && !!data?.active);
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch(`/api/academy/series`);
      if (!cancelled && ok) setPreviewSeries((data?.items || []).slice(0, 6));
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (previewSeries.length < 2) return;
    const tid = setInterval(() => {
      setSlideIdx((i) => (i + 1) % Math.min(previewSeries.length, 5));
    }, 5000);
    return () => clearInterval(tid);
  }, [previewSeries.length]);

  if (hasSub === true) return <Navigate to="/academy/watch" replace />;

  const slides = previewSeries.slice(0, 5);
  const activeSlide = slides[slideIdx];

  return (
    <Layout testId="page-academy">
      <SEO title={t("academyM.seo.title")} description={sub(t("academyM.seo.desc"), { days: TRIAL_DAYS })} />

      {/* HERO */}
      <section className="bg-[color:var(--gv-mist)] pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-5" data-testid="academy-hero-eyebrow">
              <Sparkles size={12} /> geoVINUM Academy
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[color:var(--gv-ink)] mb-4" data-testid="academy-hero-title">
              {t("academyM.hero.title")}
            </h1>
            <p className="text-[15px] md:text-[17px] text-[color:var(--gv-ink)]/75 leading-relaxed max-w-2xl mb-5" data-testid="academy-hero-desc">
              {t("academyM.hero.desc")}
            </p>
            <p className="text-[14.5px] text-[color:var(--gv-pink)] font-medium mb-7" data-testid="academy-hero-binge">
              {t("academyM.hero.binge")}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="academy-hero-cta-primary">
                <Sparkles size={14} /> {sub(t("academyM.hero.cta_primary"), { days: TRIAL_DAYS })} <ArrowRight size={14} />
              </a>
              <a href="#preview" className="btn-secondary" data-testid="academy-hero-cta-secondary">{t("academyM.hero.cta_secondary")}</a>
            </div>
            <div className="text-[12.5px] text-[color:var(--gv-ink)]/55 flex items-center gap-3">
              <span className="flex items-center gap-1"><Check size={12} className="text-[color:var(--gv-pink)]" /> {t("academyM.hero.trust.no_commitment")}</span>
              <span className="flex items-center gap-1"><Check size={12} className="text-[color:var(--gv-pink)]" /> {t("academyM.hero.trust.cancel_anytime")}</span>
              <span className="flex items-center gap-1"><Check size={12} className="text-[color:var(--gv-pink)]" /> {t("academyM.hero.trust.hd_streaming")}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroSlider slides={slides} activeIdx={slideIdx} onSelect={setSlideIdx} fallback={activeSlide} t={t} />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 bg-white" data-testid="academy-why">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("academyM.why.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-3">{t("academyM.why.title")}</h2>
            <p className="text-[14.5px] text-[color:var(--gv-ink)]/70">{t("academyM.why.desc")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Why icon={<BookOpen size={20} />} title={t("academyM.why.visual.title")} desc={t("academyM.why.visual.desc")} />
            <Why icon={<GraduationCap size={20} />} title={t("academyM.why.wset.title")} desc={t("academyM.why.wset.desc")} />
            <Why icon={<Award size={20} />} title={t("academyM.why.experts.title")} desc={t("academyM.why.experts.desc")} />
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="py-20 bg-[color:var(--gv-mist)]" data-testid="academy-audience">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("academyM.audience.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t("academyM.audience.title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Audience Icon={GraduationCap} title={t("academyM.audience.wset.title")} desc={t("academyM.audience.wset.desc")} badge={t("academyM.audience.wset.badge")} />
            <Audience Icon={Users} title={t("academyM.audience.pros.title")} desc={t("academyM.audience.pros.desc")} />
            <Audience Icon={Sparkles} title={t("academyM.audience.amateurs.title")} desc={t("academyM.audience.amateurs.desc")} />
          </div>
        </div>
      </section>

      {/* SUB-APPS STORY — 5 real captures with narrative */}
      <AcademySubAppsStory />

      {/* HOW */}
      <section className="py-20 bg-white" data-testid="academy-how">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("academyM.how.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t("academyM.how.title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Step n="1" title={t("academyM.how.step1.title")} desc={sub(t("academyM.how.step1.desc"), { days: TRIAL_DAYS })} />
            <Step n="2" title={t("academyM.how.step2.title")} desc={t("academyM.how.step2.desc")} />
            <Step n="3" title={t("academyM.how.step3.title")} desc={t("academyM.how.step3.desc")} />
          </div>
        </div>
      </section>

      {/* PREVIEW */}
      <section id="preview" className="py-20 bg-[color:var(--gv-mist)]" data-testid="academy-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("academyM.preview.eyebrow")}</div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t("academyM.preview.title")}</h2>
            </div>
            <Link to="/academy/watch" className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-pink)] hover:underline" data-testid="academy-preview-all">
              {t("academyM.preview.see_all")} <ArrowRight size={13} />
            </Link>
          </div>
          {previewSeries.length === 0 ? (
            <div className="text-center text-[color:var(--gv-ink)]/55 py-12">{t("academyM.preview.loading")}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5">
              {previewSeries.map((s) => <PreviewCard key={s.slug} s={s} t={t} />)}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-[color:var(--gv-pink)] text-white" data-testid="academy-final-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl mb-4">{sub(t("academyM.final.title"), { days: TRIAL_DAYS })}</h2>
          <p className="text-[15px] text-white/90 mb-7 max-w-xl mx-auto">
            {t("academyM.final.desc")}
          </p>
          <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white text-[color:var(--gv-pink)] px-6 py-3 rounded-[5px] font-medium hover:bg-white/90 transition-colors" data-testid="academy-final-cta-btn">
            {t("academyM.final.cta")} <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </Layout>
  );
};

const HeroSlider = ({ slides, activeIdx, onSelect, t }) => {
  if (!slides || slides.length === 0) {
    return (
      <div className="relative aspect-[4/5] rounded-[5px] overflow-hidden bg-[color:var(--gv-blue)] shadow-[0_40px_80px_-40px_rgba(46,80,103,0.4)]">
        <img src="https://images.unsplash.com/photo-1547595628-c61a29f496f0?w=1200&auto=format" alt="Academy" className="w-full h-full object-cover opacity-80" />
      </div>
    );
  }
  return (
    <div className="relative aspect-[4/5] rounded-[5px] overflow-hidden bg-[color:var(--gv-blue)] shadow-[0_40px_80px_-40px_rgba(46,80,103,0.4)]" data-testid="academy-hero-slider">
      {slides.map((s, i) => (
        <Link
          key={s.slug}
          to={`/academy/series/${s.slug}`}
          className={`absolute inset-0 transition-opacity duration-700 ${i === activeIdx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
          data-testid={`academy-hero-slide-${s.slug}`}
        >
          {s.hero_image_url ? (
            <img
              src={i === activeIdx ? s.hero_image_url : (s.hero_image_url + (s.hero_image_url.includes("?") ? "&" : "?") + "w=400&q=40")}
              alt={s.title}
              loading={i === activeIdx ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={i === activeIdx ? "high" : "low"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[color:var(--gv-blue)] to-[color:var(--gv-ink)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--gv-blue)]/90 via-[color:var(--gv-blue)]/30 to-transparent" />
          <div className="absolute bottom-6 left-5 right-5 text-white">
            <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1.5">{t("academyM.slider.featured")}</div>
            <div className="font-display text-[20px] md:text-[22px] leading-tight mb-2">{s.title}</div>
            <div className="text-[11.5px] text-white/70 flex items-center gap-2">
              <Film size={11} /> {s.videos_count} {t("academyM.slider.videos")}
              <Clock size={11} className="ml-1" /> {fmtDuration(s.total_length_sec)}
            </div>
          </div>
        </Link>
      ))}
      <div className="absolute bottom-3 right-4 z-20 flex items-center gap-1.5">
        {slides.map((s, i) => (
          <button
            key={s.slug}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(i); }}
            className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
            aria-label={sub(t("academyM.slider.goto"), { n: i + 1 })}
            data-testid={`academy-hero-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

const Why = ({ icon, title, desc }) => (
  <div className="bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-6">
    <div className="w-10 h-10 rounded-[5px] bg-white border border-[color:var(--gv-line)] flex items-center justify-center text-[color:var(--gv-pink)] mb-4">{icon}</div>
    <h3 className="font-display text-[18px] text-[color:var(--gv-ink)] mb-2">{title}</h3>
    <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{desc}</p>
  </div>
);

const Audience = ({ Icon, title, desc, badge }) => (
  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 relative">
    {badge && (
      <div className="absolute -top-3 left-5 px-2 py-0.5 bg-[color:var(--gv-pink)] text-white text-[10px] uppercase tracking-widest rounded-[5px]">{badge}</div>
    )}
    <Icon size={22} className="text-[color:var(--gv-blue)] mb-4" />
    <h3 className="font-display text-[18px] text-[color:var(--gv-ink)] mb-2">{title}</h3>
    <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div>
    <div className="text-[60px] font-display text-[color:var(--gv-pink)]/15 leading-none mb-2">{n}</div>
    <h3 className="font-display text-[18px] text-[color:var(--gv-ink)] mb-2">{title}</h3>
    <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{desc}</p>
  </div>
);

const PreviewCard = ({ s, t }) => (
  <Link to={`/academy/series/${s.slug}`} className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors" data-testid={`academy-preview-card-${s.slug}`}>
    <div className="aspect-video bg-[color:var(--gv-mist)] relative overflow-hidden">
      {s.hero_image_url ? (
        <img src={s.hero_image_url} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[color:var(--gv-ink)]/30"><Film size={28} /></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 backdrop-blur flex items-center justify-center">
        <Lock size={12} className="text-white" />
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-display text-[14.5px] text-[color:var(--gv-ink)] mb-1.5 line-clamp-2 group-hover:text-[color:var(--gv-pink)] transition-colors">{s.title}</h3>
      <div className="text-[11px] text-[color:var(--gv-ink)]/55 flex items-center gap-2">
        <Film size={11} /> {s.videos_count}
        <Clock size={11} className="ml-1" /> {fmtDuration(s.total_length_sec)}
      </div>
    </div>
  </Link>
);

export default AcademyPage;
